import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Asterisk, Heart, MessageCircle, Send, Bookmark, Calendar, Users, Receipt, CheckCircle2, TrendingUp, Sparkles, MessageSquare, BarChart3, Megaphone, Headphones, BrainCircuit, Share2 } from "lucide-react";
import { fadeUp, revealUp } from "./animation-variants";
import { lazy, Suspense } from "react";
const HeroLiveDemo = lazy(() => import("./HeroLiveDemo"));

const stats = [
  { value: "6", label: "moduler i en" },
  { value: "AI", label: "på svenska" },
  { value: "24/7", label: "autonom drift" },
  { value: "GDPR", label: "EU-lagring" },
];

const marqueeWords = [
  "Sociala medier", "Försäljning", "Marknadsföring", "Kundservice",
  "Företagets minne", "Autonom AI", "Analytics", "Fortnox",
  "Brand voice", "Kampanjer", "Team-godkännande", "Svenska högtider",
];

const heroModules = [
  { icon: Share2, name: "Sociala medier", detail: "Automatisk publicering" },
  { icon: TrendingUp, name: "Försäljning", detail: "Prospektering på autopilot" },
  { icon: Megaphone, name: "Marknadsföring", detail: "Kampanjer som konverterar" },
  { icon: Headphones, name: "Kundservice", detail: "Smart support dygnet runt" },
  { icon: BrainCircuit, name: "Företagets minne", detail: "All er kunskap, sökbar" },
  { icon: BarChart3, name: "Analytics", detail: "Insikter i realtid" },
];

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      {/* Eyebrow / meta strip */}
      <div className="border-b border-foreground/10">
        <div className="container flex items-center justify-between gap-3 py-2.5 sm:py-3 text-[9px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.16em] md:tracking-[0.18em] text-muted-foreground font-mono">
          <span className="flex items-center gap-1.5 sm:gap-2 truncate">
            <span className="inline-block h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-secondary animate-pulse shrink-0" />
            <span className="truncate">Zensio — Stockholm</span>
          </span>
          <span className="hidden md:inline">Vol. 04 · Maj MMXXVI</span>
          <span className="hidden sm:inline truncate">Hela företaget, på svenska</span>
          <span className="sm:hidden truncate">På svenska</span>
        </div>
      </div>

      {/* Decorative oversized year — bottom right */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-6 bottom-24 hidden select-none font-display text-[10rem] md:text-[14rem] lg:text-[18rem] xl:text-[22rem] leading-none tracking-[-0.05em] text-foreground/[0.04] md:block"
      >
        ’26
      </div>

      {/* Vertical side marker — left edge */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-3 xl:left-4 top-1/2 hidden -translate-y-1/2 -rotate-90 origin-left xl:block"
      >
        <span className="font-mono text-[9px] xl:text-[10px] uppercase tracking-[0.35em] xl:tracking-[0.4em] text-muted-foreground/60 whitespace-nowrap">
          Index 01 / Hero — Introduktion
        </span>
      </div>

      <div className="container relative pt-10 pb-14 sm:pt-14 sm:pb-20 md:pt-20 md:pb-24 lg:pt-28 lg:pb-32">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* Content side */}
          <div className="space-y-8 md:space-y-10">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-secondary/20 bg-secondary/5 px-3 py-1"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-secondary/60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary" />
              </span>
              <span className="font-mono text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary">
                Autonom AI för svenska företag
              </span>
            </motion.div>

            <motion.h1
              variants={revealUp}
              initial="hidden"
              animate="visible"
              custom={0.05}
              className="font-display font-light text-foreground text-[2.75rem] xs:text-[3.25rem] sm:text-[4rem] md:text-[4.5rem] lg:text-[5rem] xl:text-[6rem] leading-[1.02] tracking-[-0.03em]"
            >
              Din kompletta
              <br />
              <span className="relative inline-block italic font-normal text-secondary">
                AI-avdelning.
                <svg
                  aria-hidden
                  viewBox="0 0 320 12"
                  className="absolute -bottom-1 left-0 w-full text-secondary/40"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 Q 80 2, 160 6 T 318 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0.12}
              className="text-lg md:text-xl leading-relaxed text-foreground/70 max-w-xl"
            >
              AI-schemaläggning av sociala medier — och fem moduler till. Zensio skapar, publicerar och följer upp inlägg på Facebook, Instagram, LinkedIn, TikTok och X, på svenska, medan du fokuserar på strategin.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0.18}
              className="flex flex-col items-start gap-5"
            >
              <Button
                size="lg"
                className="cta-base cta-lift cta-on-light group h-14 md:h-16 rounded-2xl bg-secondary px-8 md:px-10 text-base md:text-lg font-bold text-secondary-foreground hover:bg-secondary/90"
                asChild
              >
                <Link to="/register?trial=true">
                  <span>Starta gratis · 14 dagar</span>
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                </Link>
              </Button>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-foreground/60">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-secondary" strokeWidth={2} />
                  Inga kortuppgifter
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-secondary" strokeWidth={2} />
                  GDPR-säkrad
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-secondary" strokeWidth={2} />
                  Redo på 2 min
                </span>
              </div>

              <a
                href="#features"
                className="cta-ghost-light mt-1 inline-flex items-center gap-2 rounded-sm font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
              >
                <span className="h-px w-8 bg-foreground/30" />
                Se hur det fungerar
                <span>02 ↓</span>
              </a>
            </motion.div>
          </div>

          {/* Visual side — animated live calendar demo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.2, 0.7, 0.2, 1] }}
            className="min-h-[360px] sm:min-h-[420px] lg:min-h-[520px]"
          >
            <Suspense fallback={<div aria-hidden className="h-full w-full" />}>
              <HeroLiveDemo />
            </Suspense>
          </motion.div>
        </div>

        {/* Editorial product mockup spread */}
        <ProductSpread />
      </div>

      {/* Marquee ticker */}
      <div className="border-y border-foreground/10 bg-foreground/[0.02] overflow-hidden">
        <div className="flex gap-6 sm:gap-8 md:gap-10 py-3 sm:py-4 animate-[marquee_40s_linear_infinite] whitespace-nowrap will-change-transform">
          {[...marqueeWords, ...marqueeWords, ...marqueeWords].map((w, i) => (
            <span
              key={i}
              className="font-display text-base sm:text-lg md:text-xl lg:text-2xl font-light text-foreground/40 tracking-[-0.01em] inline-flex items-center gap-6 sm:gap-8 md:gap-10"
            >
              {w}
              <span className="text-secondary/60">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* Stats strip — editorial */}
      <div className="border-b border-foreground/10 bg-background">
        <div className="container grid grid-cols-2 md:grid-cols-4 divide-x divide-foreground/10">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`px-3 sm:px-4 py-5 sm:py-6 md:py-8 ${i === 0 ? "pl-0" : ""} ${i === 2 ? "md:border-l border-foreground/10 md:border-0" : ""}`}
            >
              <div className={`font-display ${i % 2 === 0 ? "italic" : ""} text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground tracking-tight leading-none`}>
                {s.value}
              </div>
              <div className="mt-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.15em] text-muted-foreground">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductSpread() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      className="relative mt-16 md:mt-24"
    >
      <div className="relative rounded-none border-y border-foreground/10 bg-secondary/[0.04] px-4 py-12 sm:px-8 sm:py-16 md:px-12 md:py-20">
        {/* Rotated circular badge */}
        <div
          aria-hidden
          className="absolute -top-8 right-4 md:-top-10 md:right-10 flex h-24 w-24 md:h-28 md:w-28 rotate-12 items-center justify-center rounded-full bg-secondary text-secondary-foreground shadow-xl"
        >
          <span className="px-2 text-center font-mono text-[10px] md:text-[11px] font-bold uppercase leading-tight tracking-tight">
            Sex moduler<br />ett verktyg
          </span>
        </div>

        <div className="grid items-end gap-10 md:grid-cols-12 md:gap-12">
          {/* Left caption */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2">
              <span className="h-px w-6 bg-foreground/30" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Figur 01
              </span>
            </div>
            <p className="mt-4 font-display text-2xl md:text-3xl leading-tight tracking-[-0.01em] text-foreground/85">
              Ett <span className="italic">flöde</span> — från inlägg till lead till betald faktura.
            </p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80">
              Socialt · Pipeline · Fortnox
            </p>
          </div>

          {/* Right: three cards showing breadth — stacked on mobile, overlapping collage on md+ */}
          <div className="md:col-span-8 relative">
            <div className="flex flex-col gap-5 md:gap-0 md:block md:relative md:h-[460px] md:max-w-none md:ml-auto md:mr-0">
              {/* Card 1 — Social post (back-left) */}
              <div className="relative md:absolute md:left-0 md:top-0 w-full md:w-[52%] md:-rotate-[3deg] rounded-sm border border-foreground/10 bg-background p-4 shadow-xl md:transition-transform md:duration-500 md:hover:rotate-0 md:hover:-translate-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-secondary-foreground">Z</div>
                  <div className="flex-1">
                    <div className="font-display text-xs font-medium text-foreground">Zensio Studio</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Instagram</div>
                  </div>
                  <div className="flex h-5 items-center gap-1 rounded-full bg-secondary/10 px-1.5 font-mono text-[8px] uppercase tracking-[0.14em] text-secondary">
                    <span className="h-1 w-1 rounded-full bg-secondary animate-pulse" />AI
                  </div>
                </div>
                <div className="mt-3 aspect-[5/4] overflow-hidden rounded-sm border border-foreground/10 bg-gradient-to-br from-secondary/15 via-primary/40 to-background">
                  <div className="flex h-full w-full items-center justify-center">
                    <p className="px-4 text-center font-display italic text-foreground/55 text-xs sm:text-sm leading-snug">
                      "Det nordiska ljuset bär hela kollektionen."
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-foreground/75 line-clamp-2">
                  Ny vecka, nytt ljus. Vi släpper <span className="font-medium">höstkollektionen</span> i morgon kl 10. ✨
                </p>
                <div className="mt-3 flex items-center justify-between border-t border-foreground/10 pt-2">
                  <div className="flex items-center gap-2.5 text-foreground/70">
                    <Heart className="h-3 w-3" strokeWidth={1.5} />
                    <MessageCircle className="h-3 w-3" strokeWidth={1.5} />
                    <Send className="h-3 w-3" strokeWidth={1.5} />
                  </div>
                  <Bookmark className="h-3 w-3 text-foreground/70" strokeWidth={1.5} />
                </div>
              </div>

              {/* Card 2 — Sales pipeline (top-right) */}
              <div className="relative md:absolute md:right-0 md:top-4 w-full md:w-[52%] md:rotate-[2.5deg] rounded-sm border border-foreground/10 bg-background p-4 shadow-2xl md:transition-transform md:duration-500 md:hover:rotate-0 md:hover:-translate-y-1">
                <div className="flex items-center justify-between border-b border-foreground/10 pb-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-secondary" strokeWidth={1.8} />
                    <span className="font-display text-xs font-medium text-foreground">Pipeline</span>
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">v.42</span>
                </div>
                <div className="mt-3 space-y-2">
                  {[
                    { name: "Nordfärg AB", stage: "Kvalificerad", score: 87, tone: "bg-secondary/15 text-secondary" },
                    { name: "Lundgren Bygg", stage: "Offert skickad", score: 74, tone: "bg-secondary/10 text-secondary/90" },
                    { name: "Fjäll & Form", stage: "Nytt lead", score: 62, tone: "bg-foreground/5 text-foreground/70" },
                  ].map((l) => (
                    <div key={l.name} className="rounded-sm border border-foreground/10 bg-background/60 p-2">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-[11px] font-medium text-foreground truncate">{l.name}</span>
                        <span className={`ml-2 shrink-0 rounded-full px-1.5 py-0.5 font-mono text-[9px] font-semibold ${l.tone}`}>{l.score}</span>
                      </div>
                      <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">{l.stage}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-1.5 border-t border-foreground/10 pt-2">
                  <TrendingUp className="h-3 w-3 text-secondary" strokeWidth={1.8} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-foreground/70">AI-poäng · auto-följupp</span>
                </div>
              </div>

              {/* Card 3 — Fortnox invoice match (front-center) */}
              <div className="relative md:absolute md:bottom-0 md:left-[38%] w-full md:w-[46%] md:-rotate-[1.5deg] rounded-sm border border-foreground/10 bg-background p-4 shadow-2xl md:transition-transform md:duration-500 md:hover:rotate-0 md:hover:-translate-y-1">
                <div className="flex items-center justify-between border-b border-foreground/10 pb-2">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-3.5 w-3.5 text-secondary" strokeWidth={1.8} />
                    <span className="font-display text-xs font-medium text-foreground">Fortnox-match</span>
                  </div>
                  <span className="flex items-center gap-1 rounded-full bg-secondary/15 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-secondary">
                    <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.5} />
                    Matchad
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Vunnen offert</div>
                    <div className="mt-0.5 font-display font-medium text-foreground truncate">Nordfärg AB</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Faktura #2841</div>
                    <div className="mt-0.5 font-display font-medium text-foreground">48 500 kr</div>
                  </div>
                </div>
                <div className="mt-3 rounded-sm bg-secondary/[0.06] p-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Källa: kampanj</span>
                    <span className="font-display text-[11px] font-medium text-secondary">Höst '26</span>
                  </div>
                </div>
              </div>

              {/* Floating schedule chip — desktop only, avoids mobile stack collision */}
              <div className="hidden md:flex absolute md:left-[42%] md:-top-6 z-10 items-center gap-2 rounded-sm border border-foreground/10 bg-background px-3 py-1.5 shadow-xl rotate-[-2deg]">
                <Calendar className="h-3 w-3 text-secondary" strokeWidth={1.8} />
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-foreground/80 whitespace-nowrap">
                  Autonom drift · dygnet runt
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
